import clsx from 'clsx'
import React, { FunctionComponent, ReactNode } from 'react'

import { isDeprecationWarningEnabled } from '../foundation/Colors'
import { Icon, IconNames } from '../Icon'
import { SIZES } from '../Icon/constants'
import { DesignLanguage, Theme, useIsDesignLanguage, useTheme, useThemeClass } from '../Theme'
import { Text, TextSizes } from '../Typography'
import styles from './style.module.scss'

type LegacyColors = 'Black' | 'Light'
type SemanticColors = 'Default' | 'Inverse'

const mapLegacyColorsToSemantic: Record<LegacyColors, SemanticColors> = {
  Light: 'Default',
  Black: 'Inverse',
}

const isLegacyColor = (color: LegacyColors | SemanticColors): color is LegacyColors => {
  return Object.keys(mapLegacyColorsToSemantic).includes(color)
}

export interface BadgeProps {
  text: string | ReactNode
  color?: LegacyColors | SemanticColors
  icon?: IconNames
  testNameSpace?: string
  disabled?: boolean
}

export type BadgeComponent = FunctionComponent<BadgeProps>

const TEXT_SIZE_MAP: Record<Theme, TextSizes> = {
  [Theme.AP]: 'XS',
  [Theme.Pegasus]: 'XXS',
  [Theme.Kelpie]: 'XXS',
  [Theme.KelpieMint]: 'XXS',
  [Theme.Cash]: 'XXS',
} as const

const BaseBadge: BadgeComponent = ({ text, icon, color = 'Default', testNameSpace = '', disabled = false }) => {
  const hasTestId = testNameSpace.length > 0
  const iconDefined = typeof icon !== 'undefined' && icon !== null
  const themeClass = useThemeClass()
  const {
    theme: { selected: selectedTheme },
  } = useTheme()
  const isBronteDesign = useIsDesignLanguage(DesignLanguage.Bronte)

  const isColorLegacy = isLegacyColor(color)
  const finalColor = isColorLegacy ? mapLegacyColorsToSemantic[color] : color
  if (isDeprecationWarningEnabled() && isColorLegacy) {
    console.warn(`The color "${color}" is a legacy color and may not be supported in the future. Please use "${finalColor}" instead.`)
  }

  const containerClassName = clsx(styles.container, styles[`color${finalColor}`], disabled && styles.disabled, styles[themeClass])

  const renderIcon = (): ReactNode => {
    const IconComponent = Icon
    return iconDefined ? (
      <div className={styles.icon}>
        <IconComponent name={icon} size={SIZES.SMALL} />
      </div>
    ) : (
      <></>
    )
  }

  const renderText = (): ReactNode => {
    const shouldUseCashSansMono =
      selectedTheme === Theme.Cash || selectedTheme === Theme.Pegasus || selectedTheme === Theme.Kelpie || selectedTheme === Theme.KelpieMint

    return (
      <div className={styles.text}>
        <Text bold={isBronteDesign} size={TEXT_SIZE_MAP[selectedTheme]} fontFace={shouldUseCashSansMono ? 'CashSansMono' : undefined}>
          {text}
        </Text>
      </div>
    )
  }

  return (
    <div className={containerClassName} {...(hasTestId && { 'data-testid': `${testNameSpace}-badge` })}>
      {renderIcon()}
      {renderText()}
    </div>
  )
}

export const Badge = BaseBadge
